import React from 'react';
import {
	Box,
	Button,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export type Stage3RawRotateFileRow = {
	drive_file_id: string;
	drive_file_name: string;
	drive_mime_type: string;
	drive_relative_path: string;
	drive_parent_path: string;
	drive_modified_time: string;
	drive_view_url: string;
	rotatable: boolean;
};

type Props = {
	busy: boolean;
	folderPreset: string;
	setFolderPreset: (value: string) => void;
	folderPresetOptions: Array<{ value: string; label: string; folderId: string }>;
	baseFolderId: string;
	setBaseFolderId: (value: string) => void;
	searchQuery: string;
	setSearchQuery: (value: string) => void;
	targetFolderId: string;
	setTargetFolderId: (value: string) => void;
	toolFiles: Stage3RawRotateFileRow[];
	totalMatched: number;
	onSearch: () => void;
	onRotate: (item: Stage3RawRotateFileRow) => void;
	mergeQueue: Array<{ key: string; drive_file_id: string; drive_file_name: string; page: number }>;
	onAddMergePages: (item: Stage3RawRotateFileRow) => void;
	onRemoveMergeItem: (key: string) => void;
	onClearMergeQueue: () => void;
	onRunMerge: () => void;
	replaceBaseFileId: string;
	replaceBaseFileName: string;
	onSetReplaceBase: (item: Stage3RawRotateFileRow) => void;
	replaceMappings: Array<{
		key: string;
		target_page: number;
		source_drive_file_id: string;
		source_drive_file_name: string;
		source_page: number;
	}>;
	onAddReplaceMapping: (item: Stage3RawRotateFileRow) => void;
	onRemoveReplaceMapping: (key: string) => void;
	onClearReplaceMappings: () => void;
	onRunReplace: () => void;
};

const Stage3RawRotateTab = ({
	busy,
	folderPreset,
	setFolderPreset,
	folderPresetOptions,
	baseFolderId,
	setBaseFolderId,
	searchQuery,
	setSearchQuery,
	targetFolderId,
	setTargetFolderId,
	toolFiles,
	totalMatched,
	onSearch,
	onRotate,
	mergeQueue,
	onAddMergePages,
	onRemoveMergeItem,
	onClearMergeQueue,
	onRunMerge,
	replaceBaseFileId,
	replaceBaseFileName,
	onSetReplaceBase,
	replaceMappings,
	onAddReplaceMapping,
	onRemoveReplaceMapping,
	onClearReplaceMappings,
	onRunReplace,
}: Props) => {
	return (
		<>
			<Typography variant='subtitle1'>Narzędzia plików (raw rotate)</Typography>
			<Typography variant='caption'>
				Podaj ID katalogu bazowego Google Drive, wyszukaj pliki i zapisz obróconą kopię PDF (90/180/270) do wskazanego
				folderu.
			</Typography>

			<Stack
				direction='row'
				gap={1}
				flexWrap='wrap'
				sx={{ mt: 1 }}
			>
				<FormControl
					size='small'
					sx={{ minWidth: 360 }}
				>
					<InputLabel id='stage3-tools-folder-preset-label'>Preselect folderu</InputLabel>
					<Select
						labelId='stage3-tools-folder-preset-label'
						label='Preselect folderu'
						value={folderPreset}
						onChange={(e) => setFolderPreset(String(e.target.value || ''))}
					>
						{folderPresetOptions.map((opt) => (
							<MenuItem
								key={opt.value}
								value={opt.value}
							>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					label='ID katalogu bazowego'
					size='small'
					value={baseFolderId}
					onChange={(e) => setBaseFolderId(String(e.target.value || ''))}
					sx={{ minWidth: 360 }}
				/>
				<TextField
					label='Szukaj (nazwa/ścieżka)'
					size='small'
					value={searchQuery}
					onChange={(e) => setSearchQuery(String(e.target.value || ''))}
					sx={{ minWidth: 260 }}
				/>
				<TextField
					label='Folder zapisu (opcjonalnie)'
					size='small'
					value={targetFolderId}
					onChange={(e) => setTargetFolderId(String(e.target.value || ''))}
					sx={{ minWidth: 320 }}
				/>
				<Button
					variant='contained'
					onClick={onSearch}
					disabled={busy || baseFolderId.trim() === ''}
				>
					Wyszukaj pliki
				</Button>
			</Stack>

			<Box sx={{ mt: 1 }}>
				<Typography variant='body2'>
					Wyniki: {toolFiles.length} / {totalMatched}
				</Typography>
			</Box>

			{toolFiles.length > 0 && (
				<TableContainer
					component={Paper}
					sx={{ mt: 1, maxHeight: 420 }}
				>
					<Table
						size='small'
						stickyHeader
					>
						<TableHead>
							<TableRow>
								<TableCell>Nazwa</TableCell>
								<TableCell>Ścieżka</TableCell>
								<TableCell>Typ</TableCell>
								<TableCell>Akcje</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{toolFiles.map((item) => (
								<TableRow key={item.drive_file_id}>
									<TableCell>{item.drive_file_name}</TableCell>
									<TableCell>{item.drive_relative_path || '-'}</TableCell>
									<TableCell>{item.drive_mime_type || '-'}</TableCell>
									<TableCell>
										<Tooltip title='Podgląd na Google Drive'>
											<IconButton
												size='small'
												onClick={() => window.open(item.drive_view_url, '_blank', 'noopener,noreferrer')}
											>
												<OpenInNewIcon fontSize='small' />
											</IconButton>
										</Tooltip>
										<Tooltip
											title={
												item.rotatable
													? 'Utwórz obróconą kopię (raw rotate)'
													: 'Ten typ pliku nie jest wspierany przez raw rotate'
											}
										>
											<span>
												<IconButton
													size='small'
													onClick={() => onRotate(item)}
													disabled={busy || !item.rotatable}
												>
													<RotateRightIcon fontSize='small' />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title='Dodaj strony do kolejki scalania'>
											<span>
												<IconButton
													size='small'
													onClick={() => onAddMergePages(item)}
													disabled={busy || !item.rotatable}
												>
													<OpenInNewIcon fontSize='small' />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title='Ustaw jako dokument bazowy podmian'>
											<span>
												<Button
													size='small'
													onClick={() => onSetReplaceBase(item)}
													disabled={busy || !item.rotatable}
												>
													Baza
												</Button>
											</span>
										</Tooltip>
										<Tooltip title='Dodaj mapowanie podmiany stron z tego pliku'>
											<span>
												<Button
													size='small'
													onClick={() => onAddReplaceMapping(item)}
													disabled={busy || !item.rotatable}
												>
													Podmień
												</Button>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Box sx={{ mt: 2 }}>
				<Typography variant='subtitle2'>Scalanie stron z wielu plików (MVP)</Typography>
				<Stack
					direction='row'
					gap={1}
					flexWrap='wrap'
					sx={{ mt: 1 }}
				>
					<Button
						variant='outlined'
						onClick={onClearMergeQueue}
						disabled={busy || mergeQueue.length === 0}
					>
						Wyczyść kolejkę
					</Button>
					<Button
						variant='contained'
						onClick={onRunMerge}
						disabled={busy || mergeQueue.length === 0}
					>
						Scal i zapisz ({mergeQueue.length})
					</Button>
				</Stack>
				{mergeQueue.length > 0 && (
					<TableContainer
						component={Paper}
						sx={{ mt: 1, maxHeight: 220 }}
					>
						<Table size='small'>
							<TableHead>
								<TableRow>
									<TableCell>Lp.</TableCell>
									<TableCell>Plik</TableCell>
									<TableCell>Strona</TableCell>
									<TableCell>Akcja</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{mergeQueue.map((row, idx) => (
									<TableRow key={row.key}>
										<TableCell>{idx + 1}</TableCell>
										<TableCell>{row.drive_file_name || row.drive_file_id}</TableCell>
										<TableCell>{row.page}</TableCell>
										<TableCell>
											<IconButton
												size='small'
												onClick={() => onRemoveMergeItem(row.key)}
											>
												<DeleteOutlineIcon fontSize='small' />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>

			<Box sx={{ mt: 2 }}>
				<Typography variant='subtitle2'>Podmiana stron w dokumencie bazowym (MVP)</Typography>
				<Typography variant='body2'>Baza: {replaceBaseFileName || replaceBaseFileId || '-brak-'}</Typography>
				<Stack
					direction='row'
					gap={1}
					flexWrap='wrap'
					sx={{ mt: 1 }}
				>
					<Button
						variant='outlined'
						onClick={onClearReplaceMappings}
						disabled={busy || replaceMappings.length === 0}
					>
						Wyczyść mapowania
					</Button>
					<Button
						variant='contained'
						onClick={onRunReplace}
						disabled={busy || !replaceBaseFileId || replaceMappings.length === 0}
					>
						Podmień strony i zapisz ({replaceMappings.length})
					</Button>
				</Stack>
				{replaceMappings.length > 0 && (
					<TableContainer
						component={Paper}
						sx={{ mt: 1, maxHeight: 220 }}
					>
						<Table size='small'>
							<TableHead>
								<TableRow>
									<TableCell>Strona docelowa</TableCell>
									<TableCell>Źródło</TableCell>
									<TableCell>Strona źródłowa</TableCell>
									<TableCell>Akcja</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{replaceMappings.map((row) => (
									<TableRow key={row.key}>
										<TableCell>{row.target_page}</TableCell>
										<TableCell>{row.source_drive_file_name || row.source_drive_file_id}</TableCell>
										<TableCell>{row.source_page}</TableCell>
										<TableCell>
											<IconButton
												size='small'
												onClick={() => onRemoveReplaceMapping(row.key)}
											>
												<DeleteOutlineIcon fontSize='small' />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>
		</>
	);
};

export default Stage3RawRotateTab;
